import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Section, StateView, useAuth } from "@ozpanel/ui";
import { StatusPill } from "./PageHeader";

// The per-site log review — ONE component for both consoles, like MtlsPanel.
//
// GET /caddy/vhosts/{d}/logs (domain_admin) asks EVERY node for its own entries and merges them,
// because a site served by three nodes has three logs: a review that read only the node answering
// the request showed roughly a third of the traffic and nothing said so. Each node's answer is
// shown with its count, and a node that could not be asked is shown AS NOT ASKED — never as zero
// requests, which is what an empty list would otherwise claim.
//
// Two views of the same site:
//   requests — Caddy's access log: request line, status, size, timing, and (level "headers" or
//              above) the request/response headers.
//   bodies   — what ozcapture recorded at level "bodies": request/response bodies, capped.
// Everything is shown AS SENT — nothing is redacted anywhere (Oz, 2026-09-26).

export type SiteLogLevel = "off" | "requests" | "headers" | "bodies";

export interface CaptureSettings {
  request?: boolean;
  response?: boolean;
  methods?: string[];
  max_kb?: number;
  until?: number;
  retention_hours?: number;
  enabled_by?: string;
  enabled_at?: number;
}

interface CapturedBody {
  content_type?: string;
  size?: number;
  truncated?: boolean;
  encoding?: "text" | "base64";
  data?: string;
}

interface LogEntry {
  ts?: number;
  node?: string;
  host?: string;
  method?: string;
  uri?: string;
  proto?: string;
  ip?: string;
  remote_ip?: string;
  status?: number;
  size?: number;
  duration_ms?: number;
  req_headers?: Record<string, string>;
  resp_headers?: Record<string, string>;
  req_body?: CapturedBody;
  resp_body?: CapturedBody;
  /** Set when the site's error handling answered — that response body is not captured. */
  note?: string;
}

interface NodeAnswer {
  node: string;
  count: number;
  error?: string;
}

interface LogsResponse {
  domain: string;
  kind: "requests" | "bodies";
  items: LogEntry[] | null;
  total: number;
  nodes: NodeAnswer[] | null;
  level: SiteLogLevel;
  capture_active: boolean;
  capture: CaptureSettings | null;
  capture_available: boolean;
}

const SINCE: [string, number][] = [
  ["15 minutes", 15 * 60],
  ["1 hour", 3600],
  ["24 hours", 86400],
  ["7 days", 7 * 86400],
  ["everything kept", 0],
];

const selectClass =
  "rounded-lg border border-line2 bg-bg px-2 py-1 text-[12.5px] text-fg outline-none focus:border-accent";
const inputClass =
  "rounded-lg border border-line2 bg-bg px-2 py-1 font-mono text-[12px] text-fg outline-none focus:border-accent";

export function describeLevel(level: SiteLogLevel | undefined): string {
  switch (level) {
    case "requests":
      return "requests";
    case "headers":
      return "requests + headers";
    case "bodies":
      return "requests + headers + bodies";
    default:
      return "not logging";
  }
}

const when = (ts?: number) => (ts ? new Date(ts * 1000).toLocaleString() : "—");

export function SiteLogViewer({ domain }: { domain: string }) {
  const { client } = useAuth();
  const enc = encodeURIComponent(domain);
  const [kind, setKind] = React.useState<"requests" | "bodies">("requests");
  const [method, setMethod] = React.useState("");
  const [status, setStatus] = React.useState("");
  const [path, setPath] = React.useState("");
  const [ip, setIp] = React.useState("");
  const [since, setSince] = React.useState(3600);
  const [limit, setLimit] = React.useState(200);
  const [live, setLive] = React.useState(true);
  // Keyed by the entry, not its row index: the 15-second refresh prepends new rows, and an index
  // would move the open detail onto a different request under the reader's eyes.
  const [open, setOpen] = React.useState<string | null>(null);

  // The query string is built from APPLIED filters only — typing into the path box must not fire
  // a cluster-wide fan-out per keystroke.
  const [applied, setApplied] = React.useState({ path: "", ip: "" });
  const qs = React.useMemo(() => {
    const p = new URLSearchParams({ kind, limit: String(limit) });
    if (method) p.set("method", method);
    if (status) p.set("status", status);
    if (applied.path) p.set("path", applied.path);
    if (applied.ip) p.set("ip", applied.ip);
    return p.toString();
  }, [kind, limit, method, status, applied]);

  const logs = useQuery({
    queryKey: ["vhost-logs", domain, qs, since],
    // `since` is RELATIVE, so the cut-off is computed at each fetch — a window fixed when the filter
    // was chosen would keep widening under the 15-second refresh.
    queryFn: () => {
      const cut = since > 0 ? `&since=${Math.floor(Date.now() / 1000) - since}` : "";
      return client.apiFetch<LogsResponse>(`/caddy/vhosts/${enc}/logs?${qs}${cut}`);
    },
    retry: false,
    refetchInterval: live ? 15000 : false,
  });
  React.useEffect(() => setOpen(null), [qs, since]);

  const d = logs.data;
  const rows = d?.items ?? [];
  const nodes = d?.nodes ?? [];
  const unasked = nodes.filter((n) => n.error);
  const level = d?.level;
  const cap = d?.capture;

  const download = () => {
    const blob = new Blob([JSON.stringify({ domain, kind, query: qs, nodes, items: rows }, null, 2)], {
      type: "application/json",
    });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${domain}-${kind}-${new Date().toISOString().replace(/[:.]/g, "-")}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const apply = () => setApplied({ path: path.trim(), ip: ip.trim() });

  return (
    <Section
      title="Site logs"
      bodyClassName="p-0"
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill tone={level && level !== "off" ? "ok" : "neutral"}>{describeLevel(level)}</StatusPill>
          {level === "bodies" && (
            <StatusPill tone={d?.capture_active ? "warn" : "neutral"}>
              {d?.capture_active ? "capturing bodies" : "capture ended"}
            </StatusPill>
          )}
        </div>
      }
    >
      <div className="border-b border-line px-4 py-3 text-[12px] leading-relaxed text-muted">
        <CaptureNote level={level} cap={cap} active={!!d?.capture_active} available={d?.capture_available} />
      </div>

      <div className="flex flex-wrap items-end gap-2 border-b border-line px-4 py-3">
        <div className="mr-2 flex rounded-lg border border-line2 p-0.5">
          {(["requests", "bodies"] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setKind(k)}
              className={`rounded-md px-2.5 py-1 text-[12px] ${kind === k ? "bg-accent text-onaccent" : "text-muted hover:text-fg"}`}
            >
              {k === "requests" ? "Requests" : "Captured bodies"}
            </button>
          ))}
        </div>
        <select aria-label="Method" className={selectClass} value={method} onChange={(e) => setMethod(e.target.value)}>
          <option value="">any method</option>
          {["GET", "HEAD", "POST", "PUT", "PATCH", "DELETE", "OPTIONS"].map((m) => (
            <option key={m}>{m}</option>
          ))}
        </select>
        <select aria-label="Status" className={selectClass} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">any status</option>
          {["2xx", "3xx", "4xx", "5xx", "401", "403", "404", "429", "500", "502", "503"].map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
        <input
          aria-label="Path contains"
          className={inputClass + " w-44"}
          placeholder="path contains…"
          value={path}
          onChange={(e) => setPath(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && apply()}
        />
        <input
          aria-label="Client IP"
          className={inputClass + " w-36"}
          placeholder="client IP / prefix"
          value={ip}
          onChange={(e) => setIp(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && apply()}
        />
        <select aria-label="Since" className={selectClass} value={since} onChange={(e) => setSince(Number(e.target.value))}>
          {SINCE.map(([l, s]) => (
            <option key={s} value={s}>
              last {l}
            </option>
          ))}
        </select>
        <select aria-label="Limit" className={selectClass} value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
          {[100, 200, 500, 1000].map((n) => (
            <option key={n} value={n}>
              newest {n}
            </option>
          ))}
        </select>
        <Button size="sm" variant="ghost" onClick={apply}>
          Apply
        </Button>
        <label className="ml-auto flex items-center gap-1.5 text-[12px] text-muted">
          <input type="checkbox" checked={live} onChange={(e) => setLive(e.target.checked)} /> refresh every 15 s
        </label>
        <Button size="sm" variant="ghost" onClick={() => void logs.refetch()}>
          Refresh
        </Button>
        <Button size="sm" variant="ghost" disabled={rows.length === 0} onClick={download}>
          Download JSON
        </Button>
      </div>

      {nodes.length > 0 && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 border-b border-line px-4 py-2 text-[11.5px]">
          <span className="text-muted">Read from:</span>
          {nodes.map((n) => (
            <span key={n.node} className={n.error ? "text-warn" : "text-muted"} title={n.error}>
              {n.node} {n.error ? "— not asked" : `· ${n.count}`}
            </span>
          ))}
          {unasked.length > 0 && (
            <span className="w-full text-warn">
              {unasked.map((n) => n.node).join(", ")} could not be asked, so its requests are missing from this list —
              not absent from the site. ({unasked[0]?.error})
            </span>
          )}
        </div>
      )}

      <StateView loading={logs.isLoading} error={logs.error} errorContext="this site's logs">
        {rows.length === 0 ? (
          <div className="px-4 py-6 text-[13px] text-muted">{emptyReason(kind, level, d?.capture_active)}</div>
        ) : (
          <div className="max-h-[70vh] overflow-auto">
            <table className="w-full text-left text-[12px]">
              <thead className="sticky top-0 z-10 bg-surface text-muted">
                <tr>
                  <th className="px-4 py-1.5 font-normal">When</th>
                  <th className="py-1.5 pr-3 font-normal">Status</th>
                  <th className="py-1.5 pr-3 font-normal">Method</th>
                  <th className="py-1.5 pr-3 font-normal">Path</th>
                  <th className="py-1.5 pr-3 font-normal">From</th>
                  <th className="py-1.5 pr-3 font-normal">Took</th>
                  <th className="py-1.5 pr-4 font-normal">Node</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((x, i) => {
                  const st = x.status ?? 0;
                  const id = `${x.ts}-${x.node}-${x.method}-${x.uri}`;
                  return (
                    <React.Fragment key={`${id}-${i}`}>
                      <tr
                        className={`cursor-pointer border-t border-line hover:bg-surface2 ${open === id ? "bg-surface2" : ""}`}
                        onClick={() => setOpen(open === id ? null : id)}
                      >
                        <td className="whitespace-nowrap px-4 py-1 text-muted">{when(x.ts)}</td>
                        <td className="py-1 pr-3">
                          <span className={st === 0 ? "text-muted" : st < 400 ? "text-ok" : st < 500 ? "text-warn" : "text-crit"}>
                            {st || "—"}
                          </span>
                        </td>
                        <td className="py-1 pr-3 font-mono">{x.method}</td>
                        <td className="max-w-[360px] truncate py-1 pr-3 font-mono" title={x.uri}>
                          {x.uri}
                          {(x.req_body || x.resp_body) && <span className="ml-2 text-accent">body</span>}
                        </td>
                        <td className="py-1 pr-3 font-mono text-muted">{x.ip ?? x.remote_ip}</td>
                        <td className="whitespace-nowrap py-1 pr-3 text-muted">
                          {x.duration_ms != null ? `${x.duration_ms} ms` : "—"}
                        </td>
                        <td className="py-1 pr-4 text-muted">{x.node}</td>
                      </tr>
                      {open === id && (
                        <tr className="bg-surface2">
                          <td colSpan={7} className="px-4 py-3">
                            <EntryDetail e={x} kind={kind} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </StateView>
    </Section>
  );
}

function emptyReason(kind: string, level?: SiteLogLevel, active?: boolean): string {
  if (!level || level === "off") {
    return "Logging is off for this site, so nothing is recorded — this is not the same as nobody visiting. Turn it on under Configure → Access & logs.";
  }
  if (kind === "bodies") {
    if (level !== "bodies") {
      return "Body capture is off for this site. Set the logging level to “bodies” under Configure → Access & logs to record request/response bodies for a limited time.";
    }
    if (!active) return "Body capture has ended for this site, and nothing that was captured is still kept.";
    return "Nothing captured yet for these filters. Only the methods selected for capture are recorded (by default POST, PUT, PATCH and DELETE).";
  }
  return "No requests match these filters in the logs still kept.";
}

function CaptureNote({
  level,
  cap,
  active,
  available,
}: {
  level?: SiteLogLevel;
  cap: CaptureSettings | null | undefined;
  active: boolean;
  available?: boolean;
}) {
  if (level === "bodies" && cap) {
    const what = [cap.request && "request", cap.response && "response"].filter(Boolean).join(" and ");
    return (
      <>
        {active ? (
          <span className="text-warn">
            Capturing {what} bodies of {(cap.methods ?? []).join(", ")} requests (up to {cap.max_kb} KB each) until{" "}
            {when(cap.until)}
          </span>
        ) : (
          <span>Body capture ended {when(cap.until)}</span>
        )}
        {cap.enabled_by && (
          <>
            {" "}— turned on by <span className="text-fg">{cap.enabled_by}</span> at {when(cap.enabled_at)}
          </>
        )}
. Bodies are recorded exactly as sent, including passwords, tokens and uploaded files, and kept{" "}
        {cap.retention_hours} h.
        {available === false && (
          <span className="block text-crit">
            The running web server does not have the body-capture module, so nothing is being captured despite this
            setting.
          </span>
        )}
      </>
    );
  }
  if (level === "headers") {
    return <>Every request is recorded with its request and response headers exactly as sent, including cookies and Authorization.</>;
  }
  if (level === "requests") {
    return <>Every request is recorded: method, path, status, size, time taken and client address — no headers or bodies.</>;
  }
  return <>Logging is off for this site, so its requests are not recorded. Its bandwidth is still counted — from the web server's metrics, not from this log.</>;
}

function EntryDetail({ e, kind }: { e: LogEntry; kind: string }) {
  const meta: [string, React.ReactNode][] = [
    ["Time", when(e.ts)],
    ["Node", e.node],
    ["Host", e.host],
    ["Request", <span className="break-all font-mono">{`${e.method ?? ""} ${e.uri ?? ""} ${e.proto ?? ""}`}</span>],
    ["Client", <span className="font-mono">{e.ip ?? e.remote_ip}</span>],
    ["Status", e.status],
    ["Size", e.size != null ? `${e.size} bytes` : undefined],
    ["Took", e.duration_ms != null ? `${e.duration_ms} ms` : undefined],
  ];
  return (
    <div className="flex flex-col gap-3 text-[12px]">
      <div className="flex items-start justify-between gap-3">
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-0.5">
          {meta
            .filter(([, v]) => v !== undefined && v !== "" && v !== null)
            .map(([k, v]) => (
              <React.Fragment key={k}>
                <dt className="text-muted">{k}</dt>
                <dd className="min-w-0">{v}</dd>
              </React.Fragment>
            ))}
        </dl>
        <CopyButton label="Copy entry" text={JSON.stringify(e, null, 2)} />
      </div>
      <div className="grid gap-3 lg:grid-cols-2">
        <Headers title="Request headers" h={e.req_headers} />
        <Headers title="Response headers" h={e.resp_headers} />
      </div>
      {kind === "bodies" && (
        <div className="grid gap-3 lg:grid-cols-2">
          <BodyView title="Request body" b={e.req_body} />
          <BodyView title="Response body" b={e.resp_body} />
        </div>
      )}
      {e.note && <div className="text-warn">{e.note}</div>}
      {kind === "requests" && !e.req_headers && !e.resp_headers && (
        <div className="text-muted">Headers are not recorded at the “requests” level.</div>
      )}
    </div>
  );
}

function Headers({ title, h }: { title: string; h?: Record<string, string> }) {
  const keys = Object.keys(h ?? {}).sort();
  if (keys.length === 0) return null;
  return (
    <div className="min-w-0">
      <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted">{title}</div>
      <table className="w-full font-mono text-[11.5px]">
        <tbody>
          {keys.map((k) => (
            <tr key={k} className="border-t border-line align-top">
              <td className="whitespace-nowrap py-0.5 pr-3 text-muted">{k}</td>
              <td className="break-all py-0.5">{h![k]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BodyView({ title, b }: { title: string; b?: CapturedBody }) {
  const [raw, setRaw] = React.useState(false);
  if (!b) {
    return (
      <div className="min-w-0">
        <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted">{title}</div>
        <div className="text-muted">Not captured (empty, or not selected for capture).</div>
      </div>
    );
  }
  let shown = b.data ?? "";
  let pretty = false;
  if (!raw && b.encoding === "text" && /json/i.test(b.content_type ?? "")) {
    try {
      shown = JSON.stringify(JSON.parse(shown), null, 2);
      pretty = true;
    } catch {
      /* truncated or not JSON — shown as captured */
    }
  }
  return (
    <div className="min-w-0">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-medium uppercase tracking-wide text-muted">{title}</span>
        <span className="text-[11px] text-muted">
          {b.content_type || "no content type"} · {b.size ?? 0} bytes
          {b.encoding === "base64" && " · binary, shown as base64"}
        </span>
        {b.truncated && <StatusPill tone="warn">truncated</StatusPill>}
        <span className="ml-auto flex gap-2">
          {pretty || raw ? (
            <button type="button" className="text-[11.5px] text-muted hover:text-fg" onClick={() => setRaw(!raw)}>
              {raw ? "formatted" : "as captured"}
            </button>
          ) : null}
          <CopyButton label="Copy" text={b.data ?? ""} />
        </span>
      </div>
      <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-all rounded-lg border border-line bg-bg p-2 font-mono text-[11.5px]">
        {shown || <span className="text-muted">(empty)</span>}
      </pre>
    </div>
  );
}

function CopyButton({ label, text }: { label: string; text: string }) {
  const [done, setDone] = React.useState(false);
  return (
    <button
      type="button"
      className="whitespace-nowrap text-[11.5px] text-muted hover:text-fg"
      onClick={() => {
        void navigator.clipboard?.writeText(text).then(() => {
          setDone(true);
          setTimeout(() => setDone(false), 1500);
        });
      }}
    >
      {done ? "copied" : label}
    </button>
  );
}
